"use client";

import { Button } from "./button";
import { cn } from "./utils";
import useEmblaCarousel, { UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
    opts?: CarouselOptions;
    plugins?: CarouselPlugin;
    orientation?: "horizontal" | "vertical";
    setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
    carouselRef: ReturnType<typeof useEmblaCarousel>[0];
    api: ReturnType<typeof useEmblaCarousel>[1];
    scrollPrev: () => void;
    scrollNext: () => void;
    canScrollPrev: boolean;
    canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
    const context = React.useContext(CarouselContext);

    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }

    return context;
}

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
    ({ orientation = "horizontal", opts, setApi, plugins, className, children, ...props }, ref) => {
        const [carouselRef, api] = useEmblaCarousel(
            {
                ...opts,
                axis: orientation === "horizontal" ? "x" : "y",
            },
            plugins
        );
        const [canScrollPrev, setCanScrollPrev] = React.useState(false);
        const [canScrollNext, setCanScrollNext] = React.useState(false);

        const onSelect = React.useCallback((api: CarouselApi) => {
            if (!api) {
                return;
            }

            setCanScrollPrev(api.canScrollPrev());
            setCanScrollNext(api.canScrollNext());
        }, []);

        const scrollPrev = React.useCallback(() => {
            api?.scrollPrev();
        }, [api]);

        const scrollNext = React.useCallback(() => {
            api?.scrollNext();
        }, [api]);

        const handleKeyDown = React.useCallback(
            (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    scrollPrev();
                } else if (event.key === "ArrowRight") {
                    event.preventDefault();
                    scrollNext();
                }
            },
            [scrollPrev, scrollNext]
        );

        React.useEffect(() => {
            if (!api || !setApi) {
                return;
            }

            setApi(api);
        }, [api, setApi]);

        React.useEffect(() => {
            if (!api) {
                return;
            }

            onSelect(api);
            api.on("reInit", onSelect);
            api.on("select", onSelect);

            return () => {
                api?.off("select", onSelect);
            };
        }, [api, onSelect]);

        return (
            <CarouselContext.Provider
                value={{
                    carouselRef,
                    api: api,
                    opts,
                    orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
                    scrollPrev,
                    scrollNext,
                    canScrollPrev,
                    canScrollNext,
                }}
            >
                <div
                    ref={ref}
                    onKeyDownCapture={handleKeyDown}
                    className={cn("relative", className)}
                    role="region"
                    aria-roledescription="carousel"
                    {...props}
                >
                    {children}
                </div>
            </CarouselContext.Provider>
        );
    }
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        const { carouselRef, orientation } = useCarousel();

        return (
            <div ref={carouselRef} className="overflow-hidden">
                <div
                    ref={ref}
                    className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)}
                    {...props}
                />
            </div>
        );
    }
);
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        const { orientation } = useCarousel();

        return (
            <div
                ref={ref}
                role="group"
                aria-roledescription="slide"
                className={cn(
                    "min-w-0 shrink-0 grow-0 basis-full",
                    orientation === "horizontal" ? "pl-4" : "pt-4",
                    className
                )}
                {...props}
            />
        );
    }
);
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
    ({ className, variant = "outline", size = "icon", ...props }, ref) => {
        const { orientation, scrollPrev, canScrollPrev } = useCarousel();

        return (
            <Button
                ref={ref}
                variant={variant}
                size={size}
                className={cn(
                    "absolute  h-8 w-8 rounded-full",
                    orientation === "horizontal"
                        ? "-left-12 top-1/2 -translate-y-1/2"
                        : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
                    className
                )}
                disabled={!canScrollPrev}
                onClick={scrollPrev}
                {...props}
            >
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Previous slide</span>
            </Button>
        );
    }
);
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
    ({ className, variant = "outline", size = "icon", ...props }, ref) => {
        const { orientation, scrollNext, canScrollNext } = useCarousel();

        return (
            <Button
                ref={ref}
                variant={variant}
                size={size}
                className={cn(
                    "absolute h-8 w-8 rounded-full",
                    orientation === "horizontal"
                        ? "-right-12 top-1/2 -translate-y-1/2"
                        : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
                    className
                )}
                disabled={!canScrollNext}
                onClick={scrollNext}
                {...props}
            >
                <ArrowRight className="h-4 w-4" />
                <span className="sr-only">Next slide</span>
            </Button>
        );
    }
);

const CarouselNextButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
    ({ className, variant = "default", size = "default", ...props }, ref) => {
        const { scrollNext, canScrollNext } = useCarousel();

        return (
            <Button
                ref={ref}
                variant={variant}
                size={size}
                className={cn(className)}
                disabled={!canScrollNext}
                onClick={scrollNext}
                {...props}
            >
                Next
            </Button>
        );
    }
);

const CarouselPreviousButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
    ({ className, variant = "secondary", size = "default", ...props }, ref) => {
        const { scrollPrev, canScrollPrev } = useCarousel();

        return (
            <Button
                ref={ref}
                variant={variant}
                size={size}
                className={cn(className)}
                disabled={!canScrollPrev}
                onClick={scrollPrev}
                {...props}
            >
                Previous
            </Button>
        );
    }
);

CarouselNext.displayName = "CarouselNext";

type CarouselStepperSize = "md" | "lg";

const DOT_SIZES: Record<CarouselStepperSize, { idle: string; active: string }> = {
    md: { idle: "size-2", active: "size-3.5" },
    lg: { idle: "size-3", active: "size-5" },
};

/** A dot per slide. The current slide's dot is bigger and darker; a keyboard-focused dot gets a ring. */
function CarouselDots({
    count,
    selected,
    onSelect,
    dotLabel,
    dotTitle,
    size = "md",
    className,
}: {
    count: number;
    selected: number;
    onSelect: (index: number) => void;
    /** Screen reader label for a dot, e.g. "Show picture 2". */
    dotLabel: (index: number) => string;
    /** Hover text for a dot, e.g. the slide's name. */
    dotTitle?: (index: number) => string;
    size?: CarouselStepperSize;
    className?: string;
}) {
    const sizes = DOT_SIZES[size];
    return (
        <div className={cn("flex flex-wrap justify-center items-center gap-1", className)}>
            {Array.from({ length: count }, (_, index) => {
                const isCurrent = index === selected;
                return (
                    <button
                        key={index}
                        type="button"
                        onClick={() => onSelect(index)}
                        aria-label={dotLabel(index)}
                        aria-current={isCurrent ? "step" : undefined}
                        title={dotTitle?.(index)}
                        className="group flex justify-center items-center p-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <span
                            className={cn(
                                "block rounded-full transition-all",
                                isCurrent
                                    ? cn(sizes.active, "bg-slate-800 dark:bg-slate-100")
                                    : cn(
                                          sizes.idle,
                                          "bg-slate-300 group-hover:bg-slate-500 dark:bg-slate-600 dark:group-hover:bg-slate-400"
                                      )
                            )}
                        />
                    </button>
                );
            })}
        </div>
    );
}

const STEP_BUTTON_SIZES: Record<CarouselStepperSize, string> = {
    md: "h-10 px-4 text-sm gap-1.5",
    lg: "h-11 pl-2 pr-3 text-lg gap-1",
};

/**
 * Back and Next either side of a position count and dots, for moving through a carousel one slide at a time.
 * "lg" is the patient-facing size.
 */
function CarouselStepper({
    selected,
    total,
    onSelect,
    previousLabel,
    nextLabel,
    position,
    dotLabel,
    dotTitle,
    size = "md",
    children,
    className,
}: {
    selected: number;
    total: number;
    onSelect: (index: number) => void;
    previousLabel: string;
    nextLabel: string;
    /** Text such as "Picture 2 of 5", announced when the slide changes. */
    position: string;
    dotLabel: (index: number) => string;
    dotTitle?: (index: number) => string;
    size?: CarouselStepperSize;
    /** Shown above the position count, such as the slide's name. */
    children?: React.ReactNode;
    className?: string;
}) {
    if (total < 2) return null;
    const buttonClass = cn(
        "flex items-center border-2 border-slate-300 dark:border-slate-600 rounded-full font-medium bg-background",
        "hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        STEP_BUTTON_SIZES[size]
    );
    const iconSize = size === "lg" ? 20 : 18;

    return (
        <div className={cn("items-center gap-3 grid grid-cols-[1fr_auto_1fr]", className)}>
            <button
                type="button"
                onClick={() => onSelect(selected - 1)}
                disabled={selected === 0}
                className={cn(buttonClass, "justify-self-start")}
            >
                <ChevronLeft size={iconSize} aria-hidden />
                {previousLabel}
            </button>
            <div className="flex flex-col items-center gap-1.5">
                {children}
                <output aria-live="polite" className={cn("font-medium", size === "lg" ? "text-lg" : "text-sm")}>
                    {position}
                </output>
                <CarouselDots
                    count={total}
                    selected={selected}
                    onSelect={onSelect}
                    dotLabel={dotLabel}
                    dotTitle={dotTitle}
                    size={size}
                />
            </div>
            <button
                type="button"
                onClick={() => onSelect(selected + 1)}
                disabled={selected === total - 1}
                className={cn(buttonClass, "justify-self-end", size === "lg" && "pl-3 pr-2")}
            >
                {nextLabel}
                <ChevronRight size={iconSize} aria-hidden />
            </button>
        </div>
    );
}

export {
    CarouselDots,
    CarouselStepper,
    type CarouselApi,
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
    CarouselNextButton,
    CarouselPreviousButton,
};
